import React, { useState } from "react";
import { 
  FileText, 
  Search, 
  ChevronDown, 
  ChevronUp, 
  Check, 
  AlertCircle, 
  DollarSign, 
  Send, 
  Loader2, 
  PackageOpen,
  ExternalLink
} from "lucide-react";
import { usePOs } from "../hooks/usePOs";
import { useLogistics } from "../hooks/useLogistics";
import { useCustomers } from "../hooks/useCustomers";
import { useCompanyLogos } from "../hooks/useCompanyLogos";
import { useAuth } from "../context/AuthContext";
import PartialPaymentModal from "../components/modals/PartialPaymentModal";
import CreateInvoiceModal from "../components/modals/CreateInvoiceModal";
import { 
  buildQuickBooksInvoicePayload, 
  sendInvoiceToQuickBooksAPI, 
  getQuickBooksInvoiceViewUrl 
} from "../services/quickbooksService";
import { exportOrderToPdf, exportMaterialCostToPdf } from "../utils/invoicePdf";
import { isPoDelivered } from "../utils/deliveryStatus";
import { calculateVendorShippingMultipliers, recalculateItemClientPrice, calculateTotalCostSum, calculateNewDecimal } from "../utils/pricingUtils";

export default function Financials() {
  const { pos, loading: posLoading, updatePO } = usePOs();
  const { shipments, loading: logLoading } = useLogistics();
  const { customers, loading: custLoading } = useCustomers();
  const { currentUser } = useAuth();
  const { companyLogos } = useCompanyLogos();

  const [searchTerm, setSearchTerm] = useState("");
  const [companyFilter, setCompanyFilter] = useState("All");

  const getPoLogoUrl = (po) => {
    if (po?.logoUrl) return po.logoUrl;
    const activeLogo = companyLogos.find(l => l.isActive);
    return activeLogo?.url || "";
  };

  const getCompanyNameForPo = (po) => {
    if (!po?.logoUrl) {
      const activeLogo = companyLogos.find(l => l.isActive);
      return activeLogo?.name || "No Company";
    }
    const logo = companyLogos.find(l => l.url === po.logoUrl);
    return logo?.name || "Unknown Company";
  };
  const [activeTab, setActiveTab] = useState("open"); // "open" | "paid"
  const [expandedPOs, setExpandedPOs] = useState({});
  const [partialModal, setPartialModal] = useState({ open: false, po: null, totalOwed: 0, paidAmount: 0 });
  const [invoiceModal, setInvoiceModal] = useState({ open: false, po: null, customer: null, company: null });
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [toast, setToast] = useState({ message: "", type: "" }); // { message, type: "success" | "error" }

  const loading = posLoading || logLoading || custLoading;

  const showToast = (message, type = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast({ message: "", type: "" }), 4000);
  };

  // Helper payment calculation logic
  const calculateTotalCostSum = (items) => {
    if (!items) return 0;
    return items.reduce((sum, item) => {
      const lineCost = (Number(item.quantity || 0) * Number(item.vendorPrice || 0)) + Number(item.shippingCost || 0);
      return sum + lineCost;
    }, 0);
  };

  const getPoMarkupRate = (po) => {
    if (po && po.markupRate !== undefined && po.markupRate !== null) {
      return parseFloat(po.markupRate);
    }
    const savedMarkup = localStorage.getItem("global_markup_rate");
    return savedMarkup !== null ? parseFloat(savedMarkup) : 15;
  };

  const getPoCcFee = () => {
    const savedFee = localStorage.getItem("global_cc_fee") || localStorage.getItem("global_non_cc_discount");
    return savedFee !== null ? parseFloat(savedFee) : 3;
  };

  const isCcPayment = (po) => {
    return Boolean(po && po.paymentMethod && po.paymentMethod.trim().toLowerCase() === "credit card");
  };

  const getEffectivePoMarkupRate = (po) => {
    const baseMarkup = getPoMarkupRate(po);
    if (isCcPayment(po)) {
      const ccFee = getPoCcFee();
      return baseMarkup + ccFee;
    }
    return baseMarkup;
  };

  const calculateProposedClientCharge = (po) => {
    if (!po || !po.items) return 0;
    const markupRate = getEffectivePoMarkupRate(po);
    const multipliers = calculateVendorShippingMultipliers(po.items);
    const shippingOut = Number(po.shippingCostOut !== undefined && po.shippingCostOut !== null ? po.shippingCostOut : (po.estShippingCost || 0));
    const totalCost = calculateTotalCostSum(po.items, null, multipliers);
    const newDecimal = calculateNewDecimal(shippingOut, totalCost);
    const sum = po.items.reduce((acc, item) => {
      const price = (item.unitPrice !== undefined && item.unitPrice !== null && item.unitPrice !== "")
        ? Number(item.unitPrice) 
        : recalculateItemClientPrice(item, markupRate, multipliers, null, newDecimal);
      return acc + (isNaN(price) ? 0 : price);
    }, 0);
    return Math.round(sum * 100) / 100;
  };

  const fmtCurrency = (val) => {
    return typeof val === "number" 
      ? `$${val.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` 
      : "$0.00";
  };

  // Filter delivered POs
  const deliveredPOs = pos.filter(po => {
    return isPoDelivered({ po, shipments });
  });

  // Filter by search term
  const searchedPOs = deliveredPOs.filter(po => {
    const matchesPoNum = (po.poNumber || "").toLowerCase().includes(searchTerm.toLowerCase());
    const matchesClient = (po.clientName || "").toLowerCase().includes(searchTerm.toLowerCase());
    const matchesSearch = !searchTerm || matchesPoNum || matchesClient;

    if (!matchesSearch) return false;

    const matchesCompanyAssignment = (() => {
      if (!companyLogos || companyLogos.length === 0) return true;
      if (!po.logoUrl) return true;
      return companyLogos.some(logo => logo.url === po.logoUrl);
    })();
    if (!matchesCompanyAssignment) return false;

    if (companyFilter !== "All") {
      const companyName = getCompanyNameForPo(po);
      if (companyName !== companyFilter) return false;
    }

    return true;
  });

  // Split into Open vs Paid Invoices
  const openPOs = searchedPOs.filter(po => (po.paidStatus || "Unpaid") !== "Paid");
  const paidPOs = searchedPOs.filter(po => po.paidStatus === "Paid");

  const displayedPOs = activeTab === "open" ? openPOs : paidPOs;

  const toggleAccordion = (poId) => {
    setExpandedPOs(prev => ({ ...prev, [poId]: !prev[poId] }));
  };

  // Get customer pay schedule
  const getCustomerPaySchedule = (clientName) => {
    const cust = customers.find(c => c.name === clientName);
    return cust?.paySchedule || "Net 30"; // default Net 30 if none set
  };

  // QuickBooks Integration Modal Triggers
  const handleOpenInvoiceModal = (e, po) => {
    e.stopPropagation();
    const customer = customers.find(c => c.name === po.clientName);
    const poLogoUrl = po.logoUrl || companyLogos.find(cl => cl.isActive)?.url;
    const company = companyLogos.find(l => l.url === poLogoUrl) || companyLogos.find(l => l.isActive);
    setInvoiceModal({
      open: true,
      po,
      customer,
      company
    });
  };

  const handleSendInvoiceToQuickBooks = async ({ invoiceDate, dueDate, customerPoNumber, additionalInfo, totalAmount }) => {
    const { po, customer, company } = invoiceModal;
    if (!po) return;
    try {
      setInvoiceLoading(true);
      const payload = buildQuickBooksInvoicePayload(po, { invoiceDate, dueDate, customerPoNumber, additionalInfo }, customer, company);
      const qbResult = await sendInvoiceToQuickBooksAPI(company, payload);

      const logEntry = {
        timestamp: new Date().toISOString(),
        type: "System",
        text: `Invoice #${po.poNumber} ($${totalAmount.toFixed(2)}) dispatched to QuickBooks (${company?.name || "Default Company"} - ${qbResult.environment || "sandbox"}). QB Invoice ID: ${qbResult.qbInvoiceId}.`,
        author: currentUser?.email?.split("@")[0] || "system"
      };

      const updatedLogs = [...(po.logs || []), logEntry];
      await updatePO(po.id, {
        paidStatus: "Invoiced",
        invoiceDate,
        dueDate,
        customerPoNumber: customerPoNumber || po.customerPoNumber || "",
        additionalInfo: additionalInfo || po.additionalInfo || "",
        qbInvoiceId: qbResult.qbInvoiceId,
        qbDocNumber: qbResult.docNumber || po.poNumber,
        qbViewUrl: qbResult.viewUrl || getQuickBooksInvoiceViewUrl(company?.qbCompanyId, qbResult.qbInvoiceId, company?.qbEnvironment),
        qbEnvironment: qbResult.environment || company?.qbEnvironment || "sandbox",
        qbSyncedAt: new Date().toISOString(),
        qbSyncStatus: "Synced",
        logs: updatedLogs
      });

      setInvoiceModal({ open: false, po: null, customer: null, company: null });
      showToast(`PO ${po.poNumber} marked as Invoiced and dispatched to QuickBooks!`);
    } catch (err) {
      console.error("Error sending to QuickBooks:", err);
      showToast(err.message || "Failed to send invoice to QuickBooks.", "error");
    } finally {
      setInvoiceLoading(false);
    }
  };

  // Manual payment status dropdown update
  const handleStatusSelectChange = async (e, po) => {
    e.stopPropagation();
    const newStatus = e.target.value;
    try {
      const logEntry = {
        timestamp: new Date().toISOString(),
        type: "System",
        text: `Payment status manually updated to ${newStatus}.`,
        author: currentUser?.email?.split("@")[0] || "system"
      };

      const updatedLogs = [...(po.logs || []), logEntry];
      const updates = { 
        paidStatus: newStatus,
        logs: updatedLogs
      };

      // Reset paid amount if status is manually set back to Unpaid
      if (newStatus === "Unpaid") {
        updates.paidAmount = 0;
      } else if (newStatus === "Paid") {
        updates.paidAmount = calculateProposedClientCharge(po);
      }

      await updatePO(po.id, updates);
      showToast(`Status updated to ${newStatus} for PO ${po.poNumber}`);
    } catch (err) {
      console.error(err);
      showToast("Failed to update payment status.", "error");
    }
  };

  // Partial payment modal triggers
  const handleOpenPartialModal = (e, po) => {
    e.stopPropagation();
    const totalOwed = calculateProposedClientCharge(po);
    const paidAmount = po.paidAmount || 0;
    setPartialModal({
      open: true,
      po,
      totalOwed,
      paidAmount
    });
  };

  // Handle custom partial payment save
  const handleSavePayment = async (amount, note) => {
    const { po, totalOwed, paidAmount } = partialModal;
    try {
      const newPaidAmount = Math.round((paidAmount + amount) * 100) / 100;
      const isFullyPaid = newPaidAmount >= totalOwed - 0.01;
      const newStatus = isFullyPaid ? "Paid" : (po.paidStatus === "Paid" ? "Paid" : (po.paidStatus || "Unpaid"));

      const logEntry = {
        timestamp: new Date().toISOString(),
        type: "Payment",
        text: `Payment received: $${amount.toFixed(2)} (${note}). Total paid: $${newPaidAmount.toFixed(2)} / $${totalOwed.toFixed(2)}.`,
        author: currentUser?.email?.split("@")[0] || "system"
      };

      const updatedLogs = [...(po.logs || []), logEntry];
      
      await updatePO(po.id, {
        paidAmount: newPaidAmount,
        paidStatus: newStatus,
        logs: updatedLogs
      });

      setPartialModal({ open: false, po: null, totalOwed: 0, paidAmount: 0 });
      showToast(isFullyPaid 
        ? `PO ${po.poNumber} has been fully paid!` 
        : `Recorded partial payment of $${amount.toFixed(2)} for PO ${po.poNumber}`
      );
    } catch (err) {
      console.error(err);
      showToast("Failed to record payment.", "error");
    }
  };

  return (
    <div className="animate-fade-in">
      <div className="page-header">
        <div className="page-header-info">
          <h1>Financials & Invoicing</h1>
          <p>Track client billing, record payments, and manage invoicing schedules for delivered shipments.</p>
        </div>
      </div>

      {toast.message && (
        <div 
          className={`alert ${toast.type === "error" ? "alert-danger" : "alert-success"}`} 
          style={{ 
            position: "fixed", 
            top: "20px", 
            right: "20px", 
            zIndex: 1000, 
            boxShadow: "var(--shadow-lg)",
            display: "flex",
            alignItems: "center",
            gap: "0.5rem"
          }}
        >
          {toast.type === "error" ? <AlertCircle size={18} /> : <Check size={18} />}
          <span>{toast.message}</span>
        </div>
      )}

      {/* Tabs Layout */}
      <div style={{ display: "flex", borderBottom: "1px solid var(--border-color)", marginBottom: "1.5rem", gap: "1rem" }}>
        <button 
          onClick={() => setActiveTab("open")}
          style={{ 
            background: "none", 
            border: "none", 
            borderBottom: activeTab === "open" ? "2px solid var(--primary)" : "2px solid transparent", 
            padding: "0.75rem 1.25rem", 
            color: activeTab === "open" ? "var(--primary)" : "var(--text-secondary)", 
            fontWeight: 600, 
            cursor: "pointer",
            fontSize: "0.95rem"
          }}
        >
          Open Invoices ({openPOs.length})
        </button>
        <button 
          onClick={() => setActiveTab("paid")}
          style={{ 
            background: "none", 
            border: "none", 
            borderBottom: activeTab === "paid" ? "2px solid var(--primary)" : "2px solid transparent", 
            padding: "0.75rem 1.25rem", 
            color: activeTab === "paid" ? "var(--primary)" : "var(--text-secondary)", 
            fontWeight: 600, 
            cursor: "pointer",
            fontSize: "0.95rem"
          }}
        >
          Paid Invoices ({paidPOs.length})
        </button>
      </div>

      {/* Search Bar */}
      <div className="proc-search-bar" style={{ display: "flex", gap: "1rem", alignItems: "center", marginBottom: "1.5rem" }}>
        <div className="proc-search-box" style={{ flex: 1 }}>
          <Search size={18} className="proc-search-icon" />
          <input
            type="text"
            className="form-input proc-search-input"
            placeholder="Search delivered POs by PO # or Customer Name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ margin: 0 }}
          />
        </div>
        <select
          value={companyFilter}
          onChange={(e) => setCompanyFilter(e.target.value)}
          className="form-input"
          style={{ width: "220px", height: "42px", margin: 0, padding: "0.25rem 0.5rem", backgroundColor: "var(--bg-card)" }}
        >
          <option value="All">All Companies</option>
          {companyLogos.map(logo => (
            <option key={logo.id} value={logo.name}>{logo.name}</option>
          ))}
        </select>
      </div>

      {/* Invoices List Accordion */}
      <div className="proc-accordion-list">
        {loading ? (
          <div className="proc-loading">
            <Loader2 className="proc-spinner" size={36} />
            <span>Loading Delivered Orders...</span>
          </div>
        ) : displayedPOs.length === 0 ? (
          <div className="proc-empty">
            <PackageOpen size={48} className="proc-empty-icon" />
            <h3>No Invoices Found</h3>
            <p>
              {activeTab === "open" 
                ? "There are no open or invoiced POs currently outstanding." 
                : "No fully paid invoices have been recorded yet."}
            </p>
          </div>
        ) : (
          displayedPOs.map(po => {
            const isExpanded = expandedPOs[po.id];
            const paySchedule = getCustomerPaySchedule(po.clientName);
            const totalInvoiceOwed = calculateProposedClientCharge(po);
            const totalPaidAmount = po.paidAmount || 0;
            const balanceDue = Math.max(0, totalInvoiceOwed - totalPaidAmount);
            const currentPaidStatus = po.paidStatus || "Unpaid";

            return (
              <div key={po.id} className="proc-card">
                {/* Accordion Header */}
                <div 
                  className={`proc-card-header ${isExpanded ? "proc-card-header-expanded" : ""}`} 
                  onClick={() => toggleAccordion(po.id)}
                >
                  <div className="proc-card-header-left">
                    <div className="proc-card-icon">
                      {getPoLogoUrl(po) ? (
                        <img 
                          src={getPoLogoUrl(po)} 
                          alt="Company logo" 
                          style={{ width: "24px", height: "24px", borderRadius: "4px", objectFit: "cover" }} 
                        />
                      ) : (
                        <FileText size={24} />
                      )}
                    </div>
                    <div>
                      <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap" }}>
                        <h3 className="proc-card-po-number" style={{ margin: 0 }}>
                          {po.poNumber || "Draft PO"}{po.title ? ` — ${po.title}` : ""}
                        </h3>
                        {po.isPriority && (
                          <span className="priority-pill">PRIORITY</span>
                        )}
                      </div>
                      <p className="proc-card-client" style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "0.5rem", marginTop: "0.25rem" }}>
                        <span>Client: <strong>{po.clientName || "Unknown"}</strong></span>
                        {po.customerPoNumber && (
                          <span style={{ fontSize: "0.8rem", color: "var(--text-secondary)" }}>
                            • Cust PO: <strong>{po.customerPoNumber}</strong>
                          </span>
                        )}
                        <span 
                          style={{ 
                            fontSize: "0.8rem", 
                            backgroundColor: "rgba(255, 255, 255, 0.05)", 
                            padding: "0.15rem 0.45rem", 
                            borderRadius: "4px", 
                            color: "var(--primary)", 
                            border: "1px solid var(--border-color)", 
                            fontWeight: 600 
                          }}
                        >
                          {paySchedule}
                        </span>
                        {po.invoiceDate && (
                          <span style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>
                            Inv: {po.invoiceDate}
                          </span>
                        )}
                        {po.dueDate && (
                          <span style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>
                            Due: {po.dueDate}
                          </span>
                        )}
                      </p>
                    </div>
                  </div>

                  <div className="proc-card-header-right" style={{ gap: "1.5rem" }}>
                    <div className="proc-card-cost">
                      <p className="proc-card-cost-label" style={{ textAlign: "right" }}>Total Owed</p>
                      <p className="proc-card-cost-value" style={{ color: "var(--text-primary)" }}>{fmtCurrency(totalInvoiceOwed)}</p>
                    </div>

                    <div style={{ display: "flex", gap: "0.75rem", alignItems: "center" }} onClick={(e) => e.stopPropagation()}>
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        style={{ display: "flex", alignItems: "center", gap: "0.3rem", padding: "0.4rem 0.75rem" }}
                        onClick={(e) => {
                          e.stopPropagation();
                          const logoUrl = getPoLogoUrl(po);
                          const companyName = getCompanyNameForPo(po);
                          const paySchedule = getCustomerPaySchedule(po.clientName);
                          exportOrderToPdf(po, { logoUrl, companyName, paySchedule });
                        }}
                        title="Export Order Invoice to PDF"
                      >
                        <FileText size={12} /> Export Invoice
                      </button>

                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        style={{ display: "flex", alignItems: "center", gap: "0.3rem", padding: "0.4rem 0.75rem" }}
                        onClick={(e) => {
                          e.stopPropagation();
                          const logoUrl = getPoLogoUrl(po);
                          const companyName = getCompanyNameForPo(po);
                          exportMaterialCostToPdf(po, { logoUrl, companyName });
                        }}
                        title="Export Material Cost Breakdown to PDF"
                      >
                        <DollarSign size={12} /> Export Material Cost
                      </button>

                      {currentPaidStatus === "Unpaid" && (
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          style={{ display: "flex", alignItems: "center", gap: "0.3rem", padding: "0.4rem 0.75rem" }}
                          onClick={(e) => handleOpenInvoiceModal(e, po)}
                        >
                          <Send size={12} /> Send to QuickBooks
                        </button>
                      )}

                      {(currentPaidStatus === "Invoiced" || po.qbViewUrl || po.qbInvoiceId) && (
                        <a
                          href={po.qbViewUrl || getQuickBooksInvoiceViewUrl(po.qbCompanyId, po.qbInvoiceId, po.qbEnvironment)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-secondary btn-sm"
                          style={{ 
                            display: "flex", 
                            alignItems: "center", 
                            gap: "0.3rem", 
                            padding: "0.4rem 0.75rem", 
                            textDecoration: "none", 
                            color: "var(--text-primary)",
                            backgroundColor: "rgba(16, 185, 129, 0.1)",
                            borderColor: "rgba(16, 185, 129, 0.3)"
                          }}
                          onClick={(e) => e.stopPropagation()}
                          title="Open and review this invoice in QuickBooks Online"
                        >
                          <ExternalLink size={12} style={{ color: "var(--success)" }} /> Open in QuickBooks
                        </a>
                      )}

                      <select
                        className="form-input"
                        style={{ margin: 0, width: "120px", height: "35px", padding: "0 0.5rem", fontSize: "0.85rem" }}
                        value={currentPaidStatus}
                        onChange={(e) => handleStatusSelectChange(e, po)}
                      >
                        <option value="Unpaid">Unpaid</option>
                        <option value="Invoiced">Invoiced</option>
                        <option value="Paid">Paid</option>
                      </select>
                    </div>

                    {isExpanded ? <ChevronUp size={20} color="var(--text-secondary)" /> : <ChevronDown size={20} color="var(--text-secondary)" />}
                  </div>
                </div>

                {/* Accordion Body */}
                {isExpanded && (
                  <div className="proc-card-body">
                    <div className="proc-body-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "0.5rem" }}>
                      <h4>Invoice Line Items Summary</h4>
                      {po.qbInvoiceId && (
                        <div style={{ fontSize: "0.8rem", color: "var(--text-secondary)", display: "flex", alignItems: "center", gap: "0.5rem" }}>
                          <span>QuickBooks Reference: <strong style={{ color: "var(--text-primary)" }}>{po.qbInvoiceId}</strong></span>
                          {po.qbEnvironment && (
                            <span style={{ 
                              fontSize: "0.7rem", 
                              padding: "2px 6px", 
                              borderRadius: "4px", 
                              backgroundColor: (po.qbEnvironment === "production") ? "rgba(59, 130, 246, 0.2)" : "rgba(245, 158, 11, 0.2)",
                              color: (po.qbEnvironment === "production") ? "#60a5fa" : "#fbbf24",
                              border: `1px solid ${(po.qbEnvironment === "production") ? "rgba(59, 130, 246, 0.4)" : "rgba(245, 158, 11, 0.4)"}`,
                              textTransform: "uppercase",
                              fontWeight: 700
                            }}>
                              {po.qbEnvironment}
                            </span>
                          )}
                        </div>
                      )}
                    </div>

                    <div className="table-responsive">
                      <table className="proc-table">
                        <thead>
                          <tr>
                            <th>Description</th>
                            <th className="text-center" style={{ width: "80px" }}>Qty</th>
                            <th className="text-right" style={{ width: "120px" }}>Vendor Cost</th>
                            <th className="text-right" style={{ width: "100px" }}>Shipping</th>
                            <th className="text-center" style={{ width: "100px" }}>Markup Rate</th>
                            <th className="text-right" style={{ width: "130px" }}>Client Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {po.items && po.items.map((item, idx) => {
                            const markupRate = getEffectivePoMarkupRate(po);
                            const multipliers = calculateVendorShippingMultipliers(po.items);
                            const shippingOut = Number(po.shippingCostOut !== undefined && po.shippingCostOut !== null ? po.shippingCostOut : (po.estShippingCost || 0));
                            const totalCost = calculateTotalCostSum(po.items, null, multipliers);
                            const newDecimal = calculateNewDecimal(shippingOut, totalCost);
                            const clientTotal = (item.unitPrice !== undefined && item.unitPrice !== null && item.unitPrice !== "")
                              ? Number(item.unitPrice) 
                              : recalculateItemClientPrice(item, markupRate, multipliers, null, newDecimal);

                            return (
                              <tr key={idx}>
                                <td style={{ color: "var(--text-primary)" }}>{item.description || "No description"}</td>
                                <td className="text-center">{item.quantity}</td>
                                <td className="text-right">{fmtCurrency(Number(item.vendorPrice || 0))}</td>
                                <td className="text-right">{fmtCurrency(Number(item.shippingCost || 0))}</td>
                                <td className="text-center">
                                  {item.isMarkupCustom
                                    ? (item.markupType === "dollar" && item.markupDollar !== undefined && item.markupDollar !== null
                                        ? `$${Number(item.markupDollar).toFixed(2)}`
                                        : `${item.markupPercentage}%`)
                                    : `${markupRate}%`}
                                </td>
                                <td className="text-right" style={{ color: "var(--text-primary)", fontWeight: 600 }}>
                                  {fmtCurrency(clientTotal)}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>

                    {/* Invoice Payment Tracker */}
                    <div style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr", gap: "2rem", marginTop: "1.5rem", borderTop: "1px solid var(--border-color)", paddingTop: "1.5rem" }}>
                      {/* Left: Payment Logs history */}
                      <div>
                        <h4 style={{ margin: "0 0 0.75rem 0", color: "var(--text-secondary)", fontSize: "0.95rem" }}>Payment Receipt Notes</h4>
                        <div 
                          style={{ 
                            maxHeight: "150px", 
                            overflowY: "auto", 
                            backgroundColor: "rgba(0, 0, 0, 0.05)", 
                            padding: "0.75rem", 
                            borderRadius: "6px", 
                            border: "1px dashed var(--border-color)"
                          }}
                        >
                          {po.logs && po.logs.filter(log => log.type === "Payment").length > 0 ? (
                            po.logs.filter(log => log.type === "Payment").map((log, idx) => (
                              <div key={idx} style={{ paddingBottom: "0.5rem", marginBottom: "0.5rem", borderBottom: "1px solid var(--border-color)" }}>
                                <span style={{ fontSize: "0.7rem", color: "var(--text-muted)", display: "block" }}>
                                  {new Date(log.timestamp).toLocaleString()} • Recieved By: {log.author}
                                </span>
                                <span style={{ fontSize: "0.85rem", color: "var(--text-primary)" }}>{log.text}</span>
                              </div>
                            ))
                          ) : (
                            <span style={{ fontSize: "0.85rem", color: "var(--text-muted)", fontStyle: "italic" }}>
                              No payments recorded yet.
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Right: Payment Ledger Summary */}
                      <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.9rem" }}>
                          <span style={{ color: "var(--text-secondary)" }}>Total Client Bill:</span>
                          <span style={{ color: "var(--text-primary)", fontWeight: 600 }}>{fmtCurrency(totalInvoiceOwed)}</span>
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.9rem" }}>
                          <span style={{ color: "var(--text-secondary)" }}>Total Paid:</span>
                          <span style={{ color: "var(--success)", fontWeight: 600 }}>{fmtCurrency(totalPaidAmount)}</span>
                        </div>
                        <div 
                          style={{ 
                            display: "flex", 
                            justifyContent: "space-between", 
                            fontSize: "1.05rem", 
                            fontWeight: 700, 
                            borderTop: "1px solid var(--border-color)", 
                            paddingTop: "0.5rem",
                            marginTop: "0.25rem"
                          }}
                        >
                          <span style={{ color: "var(--text-primary)" }}>Balance Due:</span>
                          <span style={{ color: balanceDue > 0 ? "var(--primary)" : "var(--success)" }}>
                            {fmtCurrency(balanceDue)}
                          </span>
                        </div>

                        {balanceDue > 0 && (
                          <button
                            type="button"
                            className="btn btn-primary"
                            style={{ 
                              display: "flex", 
                              alignItems: "center", 
                              justifyContent: "center", 
                              gap: "0.3rem", 
                              marginTop: "0.75rem", 
                              alignSelf: "flex-end",
                              padding: "0.5rem 1rem"
                            }}
                            onClick={(e) => handleOpenPartialModal(e, po)}
                          >
                            <DollarSign size={14} /> Record Partial Payment
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      <PartialPaymentModal
        isOpen={partialModal.open}
        onClose={() => setPartialModal({ open: false, po: null, totalOwed: 0, paidAmount: 0 })}
        po={partialModal.po}
        totalOwed={partialModal.totalOwed}
        paidAmount={partialModal.paidAmount}
        onSavePayment={handleSavePayment}
      />

      <CreateInvoiceModal
        isOpen={invoiceModal.open}
        onClose={() => setInvoiceModal({ open: false, po: null, customer: null, company: null })}
        po={invoiceModal.po}
        customer={invoiceModal.customer}
        company={invoiceModal.company}
        onSendInvoice={handleSendInvoiceToQuickBooks}
        loading={invoiceLoading}
      />
    </div>
  );
}
